import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { getAccessToken } from "../../../awsAuth";
import "./ShowcasePage.scss";
import CreateShowcase from "./CreateShowcase";

import plusIcon from "../../Saved/img/plus.png";

import { cacheGetStale, cacheSet } from "../../../utils/swrcache";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" && window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const TYPE_LABEL = { services: "🛠️ Послуги" };
const TYPE_OPTIONS = ["services"];

/* Preview toggles (kept for quick UI experiments) */
const SHOW_PREVIEW_TYPE = false;
const SHOW_PREVIEW_COUNT = false;
const SHOW_PREVIEW_RATING = false;
const SHOW_PREVIEW_DELETE = false;

const TTL_2MIN = 2 * 60 * 1000;
const TTL_24H = 24 * 60 * 60 * 1000;

async function buildAuthHeaders() {
  const raw = await Promise.resolve(getAccessToken());
  const token = raw ? String(raw).trim() : "";
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export default function ProfileShowcase() {
  const navigate = useNavigate();
  const { userId: paramUserId } = useParams();
  const { userId: currentUserId } = useAuth();

  const effectiveUserId = paramUserId || currentUserId;
  const isOwner = !paramUserId || paramUserId === currentUserId;

  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const railRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const cacheKey = effectiveUserId ? `profile:showcases:${effectiveUserId}` : null;

  const existingTypes = useMemo(() => {
    const s = new Set();
    if (Array.isArray(items)) for (const it of items) if (it?.type) s.add(String(it.type));
    return s;
  }, [items]);

  const orderedItems = useMemo(() => {
    if (!Array.isArray(items)) return items;
    const order = new Map(TYPE_OPTIONS.map((t, i) => [t, i]));
    return [...items].sort((a, b) => {
      const ai = order.has(a?.type) ? order.get(a?.type) : 999;
      const bi = order.has(b?.type) ? order.get(b?.type) : 999;
      if (ai !== bi) return ai - bi;
      return String(a?.createdAt || "").localeCompare(String(b?.createdAt || ""));
    });
  }, [items]);

  const visibleItems = useMemo(() => {
    if (!Array.isArray(orderedItems)) return orderedItems;
    return orderedItems.filter((x) => String(x?.type || "") === "services");
  }, [orderedItems]);

  const visibleCount = useMemo(() => {
    return Array.isArray(visibleItems) ? visibleItems.length : 0;
  }, [visibleItems]);

  const canShowFab = useMemo(() => {
    if (!isOwner) return false;
    if (items === null) return false;
    return visibleCount === 0;
  }, [isOwner, items, visibleCount]);

  useEffect(() => {
    if (!effectiveUserId) return;

    let alive = true;

    const cachedFresh = cacheKey ? cacheGetStale(cacheKey, TTL_2MIN) : null;
    const cachedAny = cacheKey ? cacheGetStale(cacheKey, TTL_24H) : null;

    if (cacheKey) {
      const cached = Array.isArray(cachedFresh)
        ? cachedFresh
        : Array.isArray(cachedAny)
          ? cachedAny
          : null;

      if (cached && alive) {
        setItems(cached);
        setErr("");
      }
    }

    (async () => {
      try {
        setErr("");
        const r = await fetch(`${API_BASE}/api/showcases/user/${encodeURIComponent(effectiveUserId)}`, {
          cache: "no-store",
        });
        if (!r.ok) throw new Error("load failed");
        const data = await r.json().catch(() => []);
        const arr = Array.isArray(data) ? data : [];
        if (alive) setItems(arr);
        if (cacheKey) cacheSet(cacheKey, arr);
      } catch {
        if (!alive) return;

        const fallback = Array.isArray(cachedFresh)
          ? cachedFresh
          : Array.isArray(cachedAny)
            ? cachedAny
            : null;

        if (fallback) {
          setItems(fallback);
        } else {
          setItems([]);
          setErr("Не вдалося завантажити вітрини");
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [effectiveUserId, cacheKey]);

  useEffect(() => {
    setActiveIndex(0);
    const el = railRef.current;
    if (!el) return;
    try {
      el.scrollTo({ left: 0, behavior: "auto" });
    } catch {}
  }, [orderedItems?.length]);

  const openCreate = () => {
    if (!currentUserId) return navigate("/login");
    setErr("");
    setCreateOpen(true);
  };

  const createFromModal = async (createdItemFromModal) => {
    if (!currentUserId) return navigate("/login");
    if (busy) return;

    const created =
      createdItemFromModal && typeof createdItemFromModal === "object"
        ? createdItemFromModal
        : null;

    const createdId = String(created?.id || "").trim();
    const createdType = String(created?.type || "").trim();

    if (!createdId) {
      setErr("Не вдалося створити вітрину");
      setCreateOpen(false);
      return;
    }

    if (createdType && existingTypes.has(createdType)) {
      setCreateOpen(false);
      navigate(`/showcase/${encodeURIComponent(createdId)}`);
      return;
    }

    setErr("");

    setItems((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      if (arr.some((x) => String(x?.id) === createdId)) return arr;
      const next = [created, ...arr];
      if (cacheKey) cacheSet(cacheKey, next);
      return next;
    });

    setCreateOpen(false);
    navigate(`/showcase/${encodeURIComponent(createdId)}`);
  };

  const deleteShowcase = async (id) => {
  if (!currentUserId) return navigate("/login");
  if (!isOwner || busy) return;

  const ok = window.confirm("Видалити цю вітрину? Дію не можна скасувати.");
  if (!ok) return;

  setBusy(true);
  try {
    setErr("");

    const authHeaders = await buildAuthHeaders();

    const r = await fetch(`${API_BASE}/api/showcases/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      credentials: "include",
    });

    const out = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(out?.error || "delete failed");

    setItems((prev) => {
      const next = Array.isArray(prev) ? prev.filter((x) => x.id !== id) : prev;
      if (cacheKey && Array.isArray(next)) cacheSet(cacheKey, next);
      return next;
    });
  } catch {
    setErr("Не вдалося видалити вітрину");
  } finally {
    setBusy(false);
  }
};

  const onRailScroll = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const w = el.clientWidth || 1;
    const idx = Math.round(el.scrollLeft / w);
    setActiveIndex((prev) => (prev === idx ? prev : idx));
  }, []);

  const scrollToIndex = useCallback((idx) => {
    const el = railRef.current;
    if (!el) return;
    const w = el.clientWidth || 0;
    const left = Math.max(0, idx) * w;
    try {
      el.scrollTo({ left, behavior: "smooth" });
    } catch {
      el.scrollLeft = left;
    }
  }, []);

  return (
    <div className="profile-content-section">
      <div className="showcaseTop"></div>

      {err && <div className="showcaseError">{err}</div>}

      {items === null ? (
        <div className="showcases-placeholder">
          <p>Завантаження…</p>
        </div>
      ) : visibleCount === 0 ? (
        <div className="showcases-placeholder">
          {isOwner ? (
            <>
              <p>Розкажіть про себе</p>
              <p className="showcases-hint">
                70% користувачів знаходять клієнтів через вітрини. Демонструйте свої послуги, фото, акції та новинки — і отримуйте замовлення регулярно.
              </p>
            </>
          ) : (
            <>
              <p>У користувача поки що немає вітрини</p>
            </>
          )}
        </div>
      ) : (
        <>
          <div
            className="showcaseRail"
            ref={railRef}
            onScroll={onRailScroll}
            role="region"
            aria-label="Свайп вітрин"
          >
            {(Array.isArray(visibleItems) ? visibleItems : []).map((sc, i) => (
              <div className="showcaseSlide" key={sc.id}>
                <div
                  className="showcaseCard showcaseCard--ref showcaseCard--preview"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/showcase/${encodeURIComponent(sc.id)}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/showcase/${encodeURIComponent(sc.id)}`);
                    }
                  }}
                >
                  <div className={`showcaseCover ${sc.coverUrl ? "" : "showcaseCover--empty"}`}>
                    {sc.coverUrl ? (
                      <>
                        <img
                          className="showcaseCover__blur"
                          src={sc.coverUrl}
                          alt=""
                          aria-hidden="true"
                        />
                        <img className="showcaseCover__img" src={sc.coverUrl} alt="" />
                        <div className="showcaseCover__fade" aria-hidden="true" />
                      </>
                    ) : (
                      <div className="showcaseCover__placeholder">Обкладинки нема</div>
                    )}

                    {isOwner && SHOW_PREVIEW_DELETE && (
                      <button
                        type="button"
                        className="showcaseCover__iconBtn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          deleteShowcase(sc.id);
                        }}
                        disabled={busy}
                        aria-label="Видалити вітрину"
                        title="Видалити"
                      >
                        🗑️
                      </button>
                    )}

                    {SHOW_PREVIEW_RATING && (
                      <div className="showcaseCover__pill">
                        <span className="showcaseCover__pillDot">★</span>
                        <span className="showcaseCover__pillText">4.9</span>
                      </div>
                    )}
                  </div>

                  <div className="showcaseInfo showcaseInfo--preview">
                    <div className="showcaseInfo__row">
                      <div className="showcaseInfo__type">
                        {SHOW_PREVIEW_TYPE ? TYPE_LABEL.services : ""}
                      </div>
                      <div className="showcaseInfo__count">
                        {SHOW_PREVIEW_COUNT ? `${i + 1}/${visibleCount}` : ""}
                      </div>
                    </div>

                    <div className="showcaseInfo__title">{sc.title || "Без назви"}</div>
                    {!!sc.description && (
                      <div className="showcaseInfo__desc">{sc.description}</div>
                    )}

                    <div className="showcaseInfo__ctaWrap">
                      <button
                        type="button"
                        className="showcaseInfo__ctaBtn"
                        aria-label="Відкрити вітрину"
                      >
                        Відкрити
                      </button>
                    </div>

                    <div className="showcaseInfo__cta" aria-hidden="true">
                      Відкрити →
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {visibleCount > 1 && (
            <div className="showcaseDots" aria-label="Індикатор сторінок">
              {(Array.isArray(visibleItems) ? visibleItems : []).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`showcaseDot ${i === activeIndex ? "showcaseDot--active" : ""}`}
                  onClick={() => scrollToIndex(i)}
                  aria-label={`Вітрина ${i + 1}`}
                  aria-current={i === activeIndex ? "true" : "false"}
                />
              ))}
            </div>
          )}
        </>
      )}

      {canShowFab && (
        <button
          className="showcase__fab showcase__fab--centerPill showcase__fab--electron"
          onClick={openCreate}
          disabled={busy}
          title="Створити вітрину"
          aria-label="Створити вітрину"
        >
          <svg
            className="showcasePillTrace"
            viewBox="0 0 300 70"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <rect
              className="showcasePillTrace__path"
              x="2.5"
              y="2.5"
              width="295"
              height="65"
              rx="33"
              ry="33"
              vectorEffect="non-scaling-stroke"
            />
          </svg>

          <img
            className="showcase__fabIcon showcase__fabIcon--autoInvert"
            src={plusIcon}
            alt=""
          />
          <span className="showcase__fabText">Створити вітрину</span>
        </button>
      )}

      {createOpen && (
        <CreateShowcase
          busy={busy}
          onClose={() => setCreateOpen(false)}
          onCreate={createFromModal}
        />
      )}
    </div>
  );
}
